/*
 * EZCustomize footer behaviour v9 (sections/ez-footer.liquid)
 * 1. Menu groups: collapsible <details> on phones (<750px), static headings above.
 * 2. Country picker: progressive enhancement of Shopify's localization <select>.
 *    Without JS (or without <dialog> support) the native select + "Update"
 *    button keep working. With JS: a button opens a centred modal with a
 *    searchable country list and a "Save & Continue" button, which submits
 *    Shopify's localization form.
 * 3. Newsletter (Klaviyo embed, "Compact signup form" setting):
 *    - keeps each heading/paragraph and the coupon code on a single line by
 *      scaling it down slightly (never below 16px headings / 10.5px text /
 *      14px code; if it would need to go smaller, it wraps instead);
 *    - checks the e-mail with the same pattern Klaviyo uses, shows the result
 *      while typing and (setting) stops the button until it passes; typo
 *      suggestions (e.g. gmial.com) never block; fails open on any error;
 *    - success step: copies the coupon and shows "Copied!" only once the
 *      clipboard write is confirmed, otherwise selects the code + explains;
 *    - Ban-124: a success step WITHOUT a coupon (the code goes out by e-mail)
 *      is replaced by the store's own "Check your inbox" panel (3d).
 * No dependencies. Safe to run more than once (theme editor re-renders sections).
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var NEWSLETTER = ".footer-container .shopify-app-block[id*='klaviyo_email_marketing_sms_form_embed_block']";
  var uid = 0;

  /* ------------------------------------------------------------------ */
  /* 1. Menu groups                                                     */
  /* ------------------------------------------------------------------ */

  function initGroups(root, mq) {
    var groups = Array.prototype.slice.call(root.querySelectorAll('.ezf-group'));

    function applyLayout() {
      var mobile = mq.matches;
      groups.forEach(function (group) {
        var summary = group.querySelector('summary');
        group.open = !mobile;
        if (!summary) return;
        if (mobile) {
          summary.removeAttribute('tabindex');
          summary.removeAttribute('role');
          summary.removeAttribute('aria-level');
        } else {
          summary.setAttribute('tabindex', '-1');
          summary.setAttribute('role', 'heading');
          summary.setAttribute('aria-level', '2');
        }
      });
    }

    groups.forEach(function (group) {
      var summary = group.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', function (event) {
        if (!mq.matches) event.preventDefault();
      });
      // Phones: only one group open at a time. Keep the tapped heading where it
      // was on screen when a group above it collapses.
      group.addEventListener('toggle', function () {
        if (!mq.matches || !group.open) return;
        var before = summary.getBoundingClientRect().top;
        groups.forEach(function (other) {
          if (other !== group && other.open) other.open = false;
        });
        var shift = summary.getBoundingClientRect().top - before;
        if (Math.abs(shift) > 1) window.scrollBy(0, shift);
      });
    });

    applyLayout();
    return applyLayout;
  }

  /* ------------------------------------------------------------------ */
  /* 2. Country picker                                                  */
  /* ------------------------------------------------------------------ */

  var ICON_CHECK = '<svg class="ezf-cs__check" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="16" height="16"><path d="m5 12.5 4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_SEARCH = '<svg class="ezf-cs__search-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="16" height="16"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="m16 16 4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  var ICON_CLOSE = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  var ICON_CHEVRON = '<svg class="ezf-cs__chevron" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="16" height="16"><path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function esc(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function normalise(text) {
    return String(text).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  var ICON_PIN = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20"><path d="M12 21s7-6.2 7-11.5a7 7 0 1 0-14 0C5 14.8 12 21 12 21z" fill="currentColor"/><circle cx="12" cy="9.5" r="2.6" fill="#fff"/></svg>';

  function initCountryPicker(form, mq) {
    var select = form.querySelector('select[name="country_code"]');
    if (!select || form.hasAttribute('data-ezf-cs-ready')) return;
    // <dialog> gives a real modal: top layer above floating widgets, focus trap, Esc.
    // Browsers without it keep the native select + Update button.
    if (typeof HTMLDialogElement !== 'function' || typeof document.createElement('dialog').showModal !== 'function') return;
    form.setAttribute('data-ezf-cs-ready', '');

    var id = 'EzfCs' + ++uid;
    var root = form.closest('[data-ezf]') || form;
    var attr = function (name, fallback) { return form.getAttribute(name) || root.getAttribute(name) || fallback; };
    var labels = {
      title: attr('data-label', 'Country / currency'),
      modalTitle: attr('data-modal-title', 'Update your preferences'),
      modalText: attr('data-modal-text', 'Select your shipping country'),
      search: attr('data-search-label', 'Search country or currency'),
      empty: attr('data-empty-label', 'No matching country'),
      save: attr('data-save-label', 'Save & Continue'),
      close: attr('data-close-label', 'Close'),
      policyText: attr('data-policy-text', ''),
      policyLabel: attr('data-policy-label', ''),
      policyUrl: attr('data-policy-url', '')
    };

    var countries = Array.prototype.map.call(select.options, function (option) {
      var text = option.textContent.replace(/\s+/g, ' ').trim();
      var match = text.match(/^(.*?)\s*\(([^)]*)\)$/);
      var name = option.getAttribute('data-name') || (match ? match[1] : text);
      var currency = option.getAttribute('data-currency') || (match ? match[2] : '');
      return { code: option.value, name: name, currency: currency, key: normalise(name + ' ' + option.value + ' ' + currency) };
    });
    var current = select.value; // country the store is showing now
    var chosen = current;       // country picked in the dialog, not saved yet

    // Ban-156: the "updating" veil shown while the page reloads in the new country (phones). Built once, outside the
    // <dialog> (which closes). Back / forward from the cache brings the page back as it was: the veil goes again.
    var veilEl = null;
    function veil(c) {
      if (!veilEl) {
        veilEl = document.createElement('div');
        veilEl.className = 'ezf-cs-veil';
        veilEl.setAttribute('role', 'status');
        veilEl.innerHTML = '<span class="ezf-cs-veil__spin" aria-hidden="true"></span><p class="ezf-cs-veil__t"></p><p class="ezf-cs-veil__s"></p>';
        document.body.appendChild(veilEl);
        window.addEventListener('pageshow', function (event) {
          if (!event.persisted) return;
          veilEl.classList.remove('is-on');
          toggle.classList.remove('is-loading');
          toggle.removeAttribute('aria-busy');
          save.classList.remove('is-loading');
          save.disabled = false;
          toggle.innerHTML = '<span class="visually-hidden">' + esc(labels.title) + ':</span>' + chip(find(current)) + ICON_CHEVRON;
        });
      }
      veilEl.querySelector('.ezf-cs-veil__t').textContent = 'Updating to ' + c.name + (c.currency ? ' \u00b7 ' + c.currency : '');
      veilEl.querySelector('.ezf-cs-veil__s').textContent = 'Prices and shipping will show for your country.';
      veilEl.classList.add('is-on');
    }

    function find(code) {
      for (var i = 0; i < countries.length; i++) if (countries[i].code === code) return countries[i];
      return countries[0];
    }

    function chip(c) {
      return '<span class="ezf-cs__code" aria-hidden="true">' + esc(c.code) + '</span>' +
        '<span class="ezf-cs__name">' + esc(c.name) + '</span>' +
        '<span class="ezf-cs__currency">' + esc(c.currency) + '</span>';
    }

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ezf-cs__toggle';
    toggle.setAttribute('aria-haspopup', 'dialog');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', id);
    toggle.innerHTML = '<span class="visually-hidden">' + esc(labels.title) + ':</span>' + chip(find(current)) + ICON_CHEVRON;

    var policy = '';
    if (labels.policyUrl && labels.policyLabel) {
      var link = '<a href="' + esc(labels.policyUrl) + '">' + esc(labels.policyLabel) + '</a>';
      policy = '<p class="ezf-cs__policy">' + (labels.policyText ? esc(labels.policyText).replace('[link]', link) : link) + '</p>';
    }

    var dialog = document.createElement('dialog');
    dialog.className = 'ezf-cs__dialog';
    dialog.id = id;
    dialog.setAttribute('aria-labelledby', id + '-title');
    dialog.setAttribute('aria-describedby', id + '-text');
    dialog.innerHTML =
      '<div class="ezf-cs__box">' +
        '<button type="button" class="ezf-cs__close" aria-label="' + esc(labels.close) + '">' + ICON_CLOSE + '</button>' +
        '<div class="ezf-cs__head">' +
          '<span class="ezf-cs__pin">' + ICON_PIN + '</span>' +
          '<div><h2 class="ezf-cs__title" id="' + id + '-title">' + esc(labels.modalTitle) + '</h2>' +
          '<p class="ezf-cs__text" id="' + id + '-text">' + esc(labels.modalText) + '</p></div>' +
        '</div>' +
        '<div class="ezf-cs__picker">' +
          '<div class="ezf-cs__search">' + ICON_SEARCH +
            '<input type="search" class="ezf-cs__input" role="combobox" aria-expanded="true" aria-autocomplete="list" aria-controls="' + id + '-list"' +
            ' aria-label="' + esc(labels.search) + '" placeholder="' + esc(labels.search) + '" autocomplete="off" spellcheck="false" enterkeyhint="done">' +
          '</div>' +
          '<ul class="ezf-cs__list" role="listbox" id="' + id + '-list" aria-label="' + esc(labels.title) + '"></ul>' +
          '<p class="ezf-cs__empty" hidden>' + esc(labels.empty) + '</p>' +
        '</div>' +
        '<button type="button" class="ezf-cs__save">' + esc(labels.save) + '</button>' +
        policy +
      '</div>';

    form.appendChild(toggle);
    form.appendChild(dialog);
    form.classList.add('is-enhanced');

    var input = dialog.querySelector('.ezf-cs__input');
    var list = dialog.querySelector('.ezf-cs__list');
    var empty = dialog.querySelector('.ezf-cs__empty');
    var save = dialog.querySelector('.ezf-cs__save');
    var visible = [];
    var active = -1;

    // Ban-157 (anh 24/10 16:07): the countries most orders go to come first, then a divider and the full A-Z list (they
    // appear again there). Orders 16/10 (Shipment export, 2,743 orders): US 70.2%, UK 12.0%, Australia 5.3%,
    // New Zealand 5.1%, Canada 4.2%, Ireland 1.4% = 98.2%. Another list can be given as data-top-countries="US,GB,…"
    // on the form or the footer. While searching, only the matches show (no group, no repeats). The labels and the
    // divider are drawn by CSS on the first item of each group, so every <li> is still an option (keys, index).
    var TOP_CODES = String(attr('data-top-countries', 'US,GB,AU,NZ,CA,IE')).toUpperCase().split(/[\s,]+/);
    var byCode = {};
    countries.forEach(function (c) { byCode[c.code] = c; });
    var tops = TOP_CODES.map(function (code) { return byCode[code]; }).filter(function (c, i, a) { return c && a.indexOf(c) === i; });

    function render(query) {
      var q = normalise(query.trim());
      var head = q || tops.length < 2 ? [] : tops;
      visible = head.concat(countries.filter(function (c) { return !q || c.key.indexOf(q) !== -1; }));
      list.classList.toggle('has-top', head.length > 0);
      list.innerHTML = visible.map(function (c, i) {
        var isChosen = c.code === chosen;
        var group = !head.length ? '' : i === 0 ? ' is-top-first' : i === head.length ? ' is-all-first' : '';
        return '<li class="ezf-cs__option' + group + (isChosen ? ' is-chosen' : '') + '" role="option" id="' + id + '-o' + i + '" data-index="' + i + '" aria-selected="' + isChosen + '">' +
          chip(c) + ICON_CHECK + '</li>';
      }).join('');
      empty.hidden = visible.length > 0;
      list.scrollTop = 0;
      var start = -1;
      for (var i = 0; i < visible.length && start < 0; i++) if (visible[i].code === chosen) start = i;   // Ban-157: first match (the top group)
      setActive(q ? 0 : Math.max(start, 0), !q && start >= head.length);
    }

    function setActive(index, center) {
      var items = list.children;
      if (active > -1 && items[active]) items[active].classList.remove('is-active');
      active = Math.max(-1, Math.min(index, items.length - 1));
      if (active === -1) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      var item = items[active];
      item.classList.add('is-active');
      input.setAttribute('aria-activedescendant', item.id);
      var top = item.offsetTop;
      var bottom = top + item.offsetHeight;
      if (center) list.scrollTop = top - list.clientHeight / 2 + item.offsetHeight / 2;
      else if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    }

    function pick(index) {
      var country = visible[index];
      if (!country) return;
      chosen = country.code;
      Array.prototype.forEach.call(list.children, function (item, i) {
        var on = visible[i].code === chosen;
        item.classList.toggle('is-chosen', on);
        item.setAttribute('aria-selected', String(on));
      });
      setActive(index);
      save.classList.toggle('is-changed', chosen !== current);
    }

    var opener = null; // element that opened the dialog, gets focus back on close

    function open(from) {
      // Already open (e.g. a second trigger fired): keep the original opener.
      if (dialog.open || !dialog.isConnected) return;
      opener = from && from.nodeType === 1 ? from : toggle;
      if (opener !== toggle) opener.setAttribute('aria-expanded', 'true');
      chosen = current;
      input.value = '';
      save.classList.remove('is-changed');
      dialog.showModal();
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('ezf-cs-lock');
      render('');
      // Ban-155: phones show the picker as a bottom sheet (ez-footer.css); a downward pull closes it, like the other
      // sheets (Estimated delivery, Photo tips, gift…). The shared swipe lives in ez-floating-buttons.js (every page).
      if (mq.matches && window.ezSwipeSheet) window.ezSwipeSheet(dialog, { sheet: '.ezf-cs__dialog[open]', bg: '.ezf-cs__none', scroll: '.ezf-cs__list', close: '.ezf-cs__close' });
      // Phones: don't pop the keyboard over the list straight away.
      if (mq.matches) {
        save.focus({ preventScroll: true });
      } else {
        input.focus({ preventScroll: true });
      }
    }

    function close() {
      if (!dialog.open) return;
      dialog.classList.add('is-closing');
      var done = function () {
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
      };
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) done();
      else setTimeout(done, mq.matches ? 300 : 160);   // Ban-155: the phone sheet slides down in 0.3 s
    }

    function submit() {
      if (chosen === current) {
        close();
        return;
      }
      select.value = chosen;
      toggle.innerHTML = '<span class="visually-hidden">' + esc(labels.title) + ':</span>' + chip(find(chosen)) + ICON_CHEVRON;
      toggle.classList.add('is-loading');
      toggle.setAttribute('aria-busy', 'true');
      save.classList.add('is-loading');
      save.disabled = true;
      // Ban-156 (anh 24/10 15:44, iPhone): opened from the footer (page scrolled to its end, Safari's toolbar tucked away),
      // "Save & Continue" made the sheet jump up before the page reloaded: the reload starts a navigation and Safari brings
      // its toolbar back, so everything fixed to the bottom of the screen moves up. From the menu / "Deliver to" the toolbar
      // was already out, so nothing moved. Phones now slide the sheet away at once and cover the page with a light veil
      // ("Updating to Canada · CAD $") until the new page arrives: whatever the toolbar does, nothing visible jumps.
      if (mq.matches) {
        veil(find(chosen));
        close();
      }
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    }

    dialog.addEventListener('close', function () {
      toggle.setAttribute('aria-expanded', 'false');
      if (opener && opener !== toggle) opener.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('ezf-cs-lock');
      if (!toggle.classList.contains('is-loading') && opener && document.contains(opener)) opener.focus({ preventScroll: true });
    });
    // Esc: animate out instead of the instant native close.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });
    // Click on the dimmed backdrop (the <dialog> itself, outside the box) closes.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    toggle.addEventListener('click', function () { open(toggle); });

    // Header country button(s) of the theme: open this same dialog so both entry
    // points look and behave the same (setting "country_header_popup"). Optional:
    // any problem here must never break the footer picker itself.
    var unbindHeader = null;
    try {
      unbindHeader = bindHeaderTriggers(root, form, dialog, open, labels.title + ': ' + find(current).name + ' (' + find(current).currency + ')');
    } catch (error) {
      unbindHeader = null;
    }

    dialog.querySelector('.ezf-cs__close').addEventListener('click', close);
    save.addEventListener('click', submit);

    input.addEventListener('input', function () { render(input.value); });
    input.addEventListener('keydown', function (event) {
      var key = event.key;
      if (key === 'ArrowDown') { event.preventDefault(); setActive(active + 1); }
      else if (key === 'ArrowUp') { event.preventDefault(); setActive(Math.max(active - 1, 0)); }
      else if (key === 'PageDown') { event.preventDefault(); setActive(active + 8); }
      else if (key === 'PageUp') { event.preventDefault(); setActive(Math.max(active - 8, 0)); }
      else if (key === 'Enter') {
        event.preventDefault();
        // First Enter picks the highlighted country, a second Enter saves.
        if (visible[active] && visible[active].code !== chosen) pick(active);
        else submit();
      }
    });

    list.addEventListener('click', function (event) {
      var item = event.target.closest('.ezf-cs__option');
      if (item) pick(Number(item.getAttribute('data-index')));
    });
    list.addEventListener('dblclick', function (event) {
      if (event.target.closest('.ezf-cs__option')) submit();
    });
    list.addEventListener('mousemove', function (event) {
      var item = event.target.closest('.ezf-cs__option');
      if (item && Number(item.getAttribute('data-index')) !== active) setActive(Number(item.getAttribute('data-index')));
    });

    // Section unload (theme editor re-render): drop the global listeners and
    // close the dialog without moving focus.
    return function cleanup() {
      if (unbindHeader) unbindHeader();
      unbindHeader = null;
      opener = null;
      if (dialog.open) dialog.close();
      document.documentElement.classList.remove('ezf-cs-lock');
    };
  }

  /* Header triggers ---------------------------------------------------- */

  // Only one footer instance owns the header triggers at a time.
  var headerOwner = null;

  function bindHeaderTriggers(root, form, dialog, open, label) {
    var selector = (root.getAttribute('data-header-triggers') || '').trim();
    if (!selector) return null;
    // Invalid selector from the theme editor: skip the header part, keep the footer.
    try {
      document.querySelector(selector);
    } catch (error) {
      if (window.console && console.warn) console.warn('[ez-footer] Ignoring invalid "Header country button" selector:', selector);
      return null;
    }
    if (headerOwner) {
      if (headerOwner.root.isConnected) return null;
      headerOwner.unbind();
    }

    function closestTrigger(node) {
      while (node && node.nodeType !== 1) node = node.parentNode;
      if (!node || !node.closest) return null;
      var match = node.closest(selector);
      // Nested matches: always use the outermost one, so one control = one button.
      while (match && match.parentElement) {
        var outer = match.parentElement.closest(selector);
        if (!outer) break;
        match = outer;
      }
      return match && !form.contains(match) && !dialog.contains(match) ? match : null;
    }

    // [element, attribute, original value or null] for everything we touch,
    // put back exactly as it was on unbind.
    var saved = [];
    function remember(el, name) {
      for (var i = 0; i < saved.length; i++) if (saved[i][0] === el && saved[i][1] === name) return;
      saved.push([el, name, el.getAttribute(name)]);
    }
    function setOnce(el, name, value) {
      if (el.hasAttribute(name)) return;
      remember(el, name);
      el.setAttribute(name, value);
    }
    function enhance(trigger) {
      if (trigger.hasAttribute('data-ezf-trigger')) return;
      setOnce(trigger, 'data-ezf-trigger', '');
      if (!/^(A|BUTTON)$/.test(trigger.tagName)) {
        setOnce(trigger, 'tabindex', '0');
        setOnce(trigger, 'role', 'button');
      }
      setOnce(trigger, 'aria-haspopup', 'dialog');
      // aria-expanded reflects this dialog while bound; the original value comes back on unbind.
      remember(trigger, 'aria-expanded');
      trigger.setAttribute('aria-expanded', 'false');
      setOnce(trigger, 'aria-controls', dialog.id);
      setOnce(trigger, 'aria-label', label);
    }
    function enhanceAll() {
      var found = [];
      Array.prototype.forEach.call(document.querySelectorAll(selector), function (el) {
        var trigger = closestTrigger(el);
        if (trigger && found.indexOf(trigger) === -1) found.push(trigger);
      });
      found.forEach(enhance);
      // Drop records of elements the theme has removed (e.g. header re-rendered).
      saved = saved.filter(function (entry) { return entry[0].isConnected; });
    }

    function stop(event) {
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    }
    function onClick(event) {
      var trigger = closestTrigger(event.target);
      if (!trigger) return;
      if (!root.isConnected) { unbind(); return; }
      stop(event);
      enhance(trigger);
      open(trigger);
    }
    function onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      var trigger = closestTrigger(event.target);
      // Native buttons/links already turn Enter/Space into a click.
      if (!trigger || /^(A|BUTTON)$/.test(trigger.tagName)) return;
      if (!root.isConnected) { unbind(); return; }
      stop(event);
      enhance(trigger);
      open(trigger);
    }

    // Capture on window: runs before every document-level and element-level
    // handler of the theme, so its old popup cannot open as well.
    window.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKeydown, true);

    // Theme editor re-renders another section (e.g. only the header): the new
    // trigger elements need the keyboard/ARIA attributes again.
    function onSectionLoad() {
      if (!root.isConnected) { unbind(); return; }
      enhanceAll();
    }
    document.addEventListener('shopify:section:load', onSectionLoad);

    enhanceAll();

    function unbind() {
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('shopify:section:load', onSectionLoad);
      saved.forEach(function (entry) {
        if (entry[2] === null) entry[0].removeAttribute(entry[1]);
        else entry[0].setAttribute(entry[1], entry[2]);
      });
      saved = [];
      if (headerOwner && headerOwner.unbind === unbind) headerOwner = null;
    }
    headerOwner = { root: root, unbind: unbind };
    return unbind;
  }

  /* ------------------------------------------------------------------ */
  /* 3. Newsletter enhancements                                         */
  /* ------------------------------------------------------------------ */

  var MIN_HEADING = 16;
  var MIN_TEXT = 10.5;
  var MIN_CODE = 14;

  function largestFont(element) {
    var max = parseFloat(getComputedStyle(element).fontSize) || 0;
    var spans = element.querySelectorAll('span, strong, em, b, i');
    for (var i = 0; i < spans.length; i++) {
      if (!spans[i].textContent.trim()) continue;
      max = Math.max(max, parseFloat(getComputedStyle(spans[i]).fontSize) || 0);
    }
    return max;
  }

  function fitLine(element, minPx) {
    element.style.zoom = '';
    element.style.whiteSpace = '';
    var available = element.clientWidth; // width of the line box while wrapping is allowed
    element.style.whiteSpace = 'nowrap';
    var needed = element.scrollWidth; // width of the text on a single line
    if (!available || needed <= available + 0.5) return;
    var ratio = Math.floor((available / needed) * 1000) / 1000;
    var minRatio = minPx / (largestFont(element) || 16);
    if (ratio < minRatio || !('zoom' in element.style)) {
      element.style.whiteSpace = ''; // too long for one line: let it wrap rather than become unreadable
      return;
    }
    element.style.zoom = String(ratio);
  }

  function isBlank(element) {
    return !element.textContent.replace(/[\s​ ﻿]/g, '');
  }

  function fitNewsletter(block) {
    var lines = block.querySelectorAll('.klaviyo-form-richtext > *');
    Array.prototype.forEach.call(lines, function (line) {
      // Klaviyo copy often contains empty paragraphs (&nbsp; / zero-width spaces) that only add height.
      if (isBlank(line)) {
        line.setAttribute('data-ezf-empty', '');
        return;
      }
      line.removeAttribute('data-ezf-empty');
      // Ban-124: a long sentence (form SG2jfZ: ~130 characters) wraps at its own size instead of shrinking to 10.5px.
      if (line.textContent.replace(/\s+/g, ' ').trim().length > 90) {
        line.style.zoom = '';
        line.style.whiteSpace = '';
        return;
      }
      fitLine(line, /^H[1-6]$/.test(line.tagName) ? MIN_HEADING : MIN_TEXT);
    });
    var code = block.querySelector('.klaviyo-coupon button > div');
    if (code) fitLine(code, MIN_CODE);
  }

  /* 3b. E-mail check before the Klaviyo button can submit ------------- */

  // Exactly the pattern Klaviyo's signup forms use (onsite JS, ClientStore bundle).
  // Sharing it means this check can never reject an address Klaviyo would accept:
  // it only shows Klaviyo's verdict earlier, in EZCustomize's own words.
  var KLAVIYO_EMAIL_RE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Suggestions only (never blocking): common misspellings of big mailbox domains.
  var DOMAIN_FIX = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gnail.com': 'gmail.com',
    'gmaill.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.cm': 'gmail.com',
    'gmal.com': 'gmail.com', 'gmail.om': 'gmail.com', 'hotmial.com': 'hotmail.com', 'hotmal.com': 'hotmail.com',
    'hotmail.co': 'hotmail.com', 'hotmail.con': 'hotmail.com', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com',
    'yahoo.co': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'outlok.com': 'outlook.com', 'outloo.com': 'outlook.com',
    'outlook.co': 'outlook.com', 'iclod.com': 'icloud.com', 'icloud.co': 'icloud.com', 'icoud.com': 'icloud.com'
  };

  function checkEmail(value) {
    var email = String(value || '').trim();
    if (!email) return { state: 'empty' };
    if (!KLAVIYO_EMAIL_RE.test(email)) return { state: 'invalid' };
    var at = email.lastIndexOf('@');
    var fix = DOMAIN_FIX[email.slice(at + 1).toLowerCase()];
    if (fix) return { state: 'suggest', suggestion: email.slice(0, at + 1) + fix };
    return { state: 'valid' };
  }

  function setNativeValue(input, value) {
    var setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    setter.call(input, value); // React-controlled input: go through the native setter
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function setDescribedBy(input, id, on) {
    var ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) { return x && x !== id; });
    if (on) ids.push(id);
    if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
    else input.removeAttribute('aria-describedby');
  }

  function initEmailCheck(block, texts, blockInvalid) {
    var touched = false;
    var last = null; // last result shown, re-applied after Klaviyo/React re-renders the field

    function parts() {
      var input = block.querySelector('input[type="email"]');
      if (!input) return null;
      var host = input.closest('[data-testid="form-component"]') || input.parentElement;
      var message = host.querySelector('.ezf-email-msg');
      if (!message) {
        message = document.createElement('p');
        message.className = 'ezf-email-msg';
        message.id = 'EzfEmailMsg' + ++uid;
        message.setAttribute('aria-live', 'polite');
        message.hidden = true;
        host.appendChild(message);
      }
      return { input: input, message: message };
    }

    // Field state: always written, on every check, so it can never go stale.
    function syncField(p, error, hint) {
      var input = p.input;
      input.toggleAttribute('data-ezf-invalid', error);
      input.toggleAttribute('data-ezf-valid', !error && last && (last.state === 'valid' || last.state === 'suggest'));
      input.setAttribute('aria-invalid', error ? 'true' : 'false');
      setDescribedBy(input, p.message.id, error || hint);
    }

    function render(p, result, force) {
      var show = touched || force;
      var error = show && (result.state === 'empty' || result.state === 'invalid');
      var hint = show && result.state === 'suggest';
      last = result;
      var message = p.message;
      var key = error ? result.state : hint ? 'suggest|' + result.suggestion : 'none';
      if (message.getAttribute('data-key') !== key) {
        // Only rebuild the message when it changes (keeps the suggestion button stable).
        message.setAttribute('data-key', key);
        message.innerHTML = '';
        message.className = 'ezf-email-msg' + (error ? ' is-error' : hint ? ' is-hint' : '');
        if (error) {
          message.textContent = result.state === 'empty' ? texts.required : texts.invalid;
        } else if (hint) {
          var bits = texts.suggest.split('[email]');
          message.appendChild(document.createTextNode(bits[0] || ''));
          var fix = document.createElement('button');
          fix.type = 'button';
          fix.className = 'ezf-email-fix';
          fix.textContent = result.suggestion;
          fix.addEventListener('mousedown', function (event) { event.preventDefault(); }); // keep focus in the field
          fix.addEventListener('click', function () {
            setNativeValue(p.input, result.suggestion);
            p.input.focus();
            render(p, checkEmail(result.suggestion), true);
          });
          message.appendChild(fix);
          message.appendChild(document.createTextNode(bits[1] || ''));
        }
        message.hidden = !(error || hint);
      }
      syncField(p, error, hint);
      // Klaviyo's React re-render after this event may rewrite aria-invalid; re-apply once it has run.
      requestAnimationFrame(function () {
        var again = parts();
        if (again && last === result) syncField(again, error, hint);
      });
    }

    function validate(force) {
      var p = parts();
      if (!p) return true;
      var result = checkEmail(p.input.value);
      render(p, result, force);
      return result.state === 'valid' || result.state === 'suggest';
    }

    block.addEventListener('input', function (event) {
      if (event.target.matches && event.target.matches('input[type="email"]')) {
        block._ezfEmail = event.target.value.trim(); // Ban-124: shown back on the "Check your inbox" panel; kept in memory only
        validate(false);
      }
    }, true);

    // On blur: drop stray spaces (a common paste issue), then show the result after a
    // short pause so a message can't push the button away from a finger mid-tap.
    var blurTimer = null;
    block.addEventListener('focusout', function (event) {
      var input = event.target;
      if (!(input.matches && input.matches('input[type="email"]'))) return;
      var trimmed = input.value.trim();
      if (trimmed !== input.value) setNativeValue(input, trimmed);
      if (!trimmed) return;
      clearTimeout(blurTimer);
      blurTimer = setTimeout(function () {
        touched = true;
        validate(false);
      }, 250);
    }, true);

    // Capture phase on an ancestor runs before Klaviyo's own (React) handlers.
    function guard(event) {
      try {
        var input = block.querySelector('input[type="email"]');
        if (!input) return; // success step: nothing to check
        block._ezfTried = true; // Ban-124: a sign-up was attempted from this block
        block._ezfEmail = input.value.trim();
        clearTimeout(blurTimer);
        touched = true;
        var trimmed = input.value.trim();
        if (trimmed !== input.value) setNativeValue(input, trimmed);
        if (validate(true) || !blockInvalid) return; // valid, or "hint only" mode: let Klaviyo decide
        event.preventDefault();
        event.stopImmediatePropagation();
        input.focus();
      } catch (error) {
        // Fail open: if anything unexpected happens here, never stand between a shopper and Klaviyo.
      }
    }
    block.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('.klaviyo-form-button')) guard(event);
    }, true);
    block.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && event.target.matches && event.target.matches('input[type="email"]')) guard(event);
    }, true);
    block.addEventListener('submit', guard, true);
  }

  /* 3c. Coupon (Klaviyo success step): copy with verified feedback ---- */

  // Resolves true only when the code is really on the clipboard.
  function copyText(text) {
    function legacyCopy() {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(area);
      var active = document.activeElement;
      area.select();
      area.setSelectionRange(0, text.length); // iOS
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (error) { ok = false; }
      document.body.removeChild(area);
      if (active && typeof active.focus === 'function') active.focus({ preventScroll: true });
      return ok;
    }
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function' && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return legacyCopy(); });
    }
    return Promise.resolve(legacyCopy());
  }

  function selectText(element) {
    try {
      var range = document.createRange();
      range.selectNodeContents(element);
      var selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) { /* ignore */ }
  }

  function enhanceCoupon(block, texts) {
    var coupon = block.querySelector('.klaviyo-coupon');
    if (!coupon || coupon.querySelector('.ezf-coupon-hint')) return;
    var tip = document.createElement('span');
    tip.className = 'ezf-copied';
    tip.setAttribute('aria-hidden', 'true');
    var hint = document.createElement('span');
    hint.className = 'ezf-coupon-hint';
    hint.setAttribute('role', 'status');
    hint.setAttribute('aria-live', 'polite');
    hint.textContent = texts.copyHint;
    coupon.appendChild(tip);
    coupon.appendChild(hint);
    var attempt = 0;
    coupon.addEventListener('click', function (event) {
      if (!event.target.closest('button')) return;
      var codeEl = coupon.querySelector('button > div');
      var code = codeEl ? codeEl.textContent.trim() : '';
      if (!code) return;
      var mine = ++attempt;
      clearTimeout(coupon._ezfTimer);
      tip.classList.remove('is-on');
      // Klaviyo also copies on click but doesn't report the outcome, so copy again
      // ourselves and only claim success once the clipboard write has resolved.
      copyText(code).then(function (ok) {
        if (mine !== attempt) return;
        coupon.classList.toggle('is-copied', ok);
        coupon.classList.toggle('is-copy-failed', !ok);
        if (ok) {
          tip.textContent = texts.copied;
          tip.classList.add('is-on');
          hint.textContent = texts.copiedHint;
          coupon._ezfTimer = setTimeout(function () { tip.classList.remove('is-on'); }, 2000);
        } else {
          // Clipboard blocked: select the code so it can be copied by hand, and say so.
          hint.textContent = texts.copyFailed;
          selectText(codeEl);
        }
      });
    });
  }

  /* 3d. Ban-124: code sent by e-mail ----------------------------------- */

  // Klaviyo's success step without a coupon box means the code goes out by e-mail.
  // Its own text is replaced by one EZ panel (same look in the footer and in the gift
  // popup, which shows this very block).
  // Ban-156 (anh 24/10 15:44): the code arrives in a Welcome email from support@ezcustomize.com, there is nothing to
  // confirm. Klaviyo's success text says "confirm", so the panel showed "Confirm your email / We sent a confirmation
  // email to…". It now always says where the code is (the "confirm" texts are kept unused, in case that flow returns).
  var MAIL_APPS = [
    [/^(gmail|googlemail)\.com$/i, 'Open Gmail', 'https://mail.google.com/mail/'],
    [/^(outlook|hotmail|live|msn)\.[a-z.]+$/i, 'Open Outlook', 'https://outlook.live.com/mail/'],
    [/^(yahoo|ymail|rocketmail)\.[a-z.]+$/i, 'Open Yahoo Mail', 'https://mail.yahoo.com/']
  ];
  var SENT_ICON = '<span class="ezf-sent__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M3.8 7.2l8.2 5.8 8.2-5.8"/></svg>' +
    '<span class="ezf-sent__tick"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg></span></span>';

  function mailApp(email) {
    var domain = (email.split('@')[1] || '').toLowerCase();
    for (var i = 0; i < MAIL_APPS.length; i++) {
      if (MAIL_APPS[i][0].test(domain)) return { label: MAIL_APPS[i][1], url: MAIL_APPS[i][2] };
    }
    return null;
  }

  function line(cls, text) {
    var p = document.createElement('p');
    p.className = cls;
    if (text) p.textContent = text;
    return p;
  }

  function span(cls, text) {
    var s = document.createElement('span');
    s.className = cls;
    s.textContent = text;
    return s;
  }

  function sentPanel(block, texts) {
    if (!block._ezfTried || block.querySelector('.ezf-sent')) return;
    var form = block.querySelector('form');
    if (!form || form.querySelector('input[type="email"]') || block.querySelector('.klaviyo-coupon')) return;
    var said = (form.textContent || '').replace(/\s+/g, ' ').trim();
    if (!said) return; // between steps
    var confirm = false; // Ban-156: was /verif|confirm/i.test(said)
    var email = /^[^@\s]+@[^@\s]+$/.test(block._ezfEmail || '') ? block._ezfEmail : '';
    var panel = document.createElement('div');
    panel.className = 'ezf-sent' + (confirm ? ' ezf-sent--confirm' : '');
    panel.setAttribute('role', 'status');
    panel.innerHTML = SENT_ICON;
    panel.appendChild(line('ezf-sent__title', confirm ? texts.confirmTitle : texts.sentTitle));
    var lead = line('ezf-sent__lead');
    if (email) {
      lead.appendChild(document.createTextNode((confirm ? texts.confirmLead : texts.sentLead) + ' '));
      var b = document.createElement('b');
      b.textContent = email;
      lead.appendChild(b);
    } else lead.textContent = confirm ? texts.confirmLeadNoEmail : texts.sentLeadNoEmail;
    panel.appendChild(lead);
    panel.appendChild(line('ezf-sent__tail', confirm ? texts.confirmTail : texts.sentTail));
    panel.appendChild(line('ezf-sent__note', texts.sentNote));
    // Ban-126: the footer's own copy (artboards FooterMobile / FooterDesktop): one line "Tap / Click Confirm in the
    // email we sent to <email>" + the Spam hint (phone: own line; 750px+: same paragraph). The lines above stay
    // for the gift popup, which moves this block into its sheet (ez-floating-buttons.js §7) and was approved as is.
    var foot = line('ezf-sent__fl');
    var tap = confirm ? texts.footTap : texts.sentLead;
    var click = confirm ? texts.footClick : texts.sentLead;
    if (!email) {
      tap = confirm ? texts.footTapNoEmail : texts.sentLeadNoEmail;
      click = confirm ? texts.footClickNoEmail : texts.sentLeadNoEmail;
    }
    foot.appendChild(span('ezf-sent__m', tap));
    foot.appendChild(span('ezf-sent__d', click));
    if (email) {
      foot.appendChild(document.createTextNode(' '));
      var fb = document.createElement('b');
      fb.textContent = email;
      foot.appendChild(fb);
      foot.appendChild(span('ezf-sent__d', '.'));
    }
    foot.appendChild(span('ezf-sent__d ezf-sent__in', ' ' + (texts.sentFrom ? texts.sentFrom + '. ' : '') + texts.footNote));
    panel.appendChild(foot);
    if (texts.sentFrom) panel.appendChild(line('ezf-sent__fn', texts.sentFrom)); // Ban-156: phone footer, own line
    panel.appendChild(line('ezf-sent__fn', texts.footNote));
    // Same height as the e-mail step it replaces (measured by the ResizeObserver below): the band does not jump.
    if (block._ezfH) block.style.setProperty('--ezf-h', block._ezfH + 'px');
    var app = email && mailApp(email);
    if (app) {
      var a = document.createElement('a');
      a.className = 'ezf-sent__app';
      a.href = app.url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.innerHTML = '<span></span><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3.5h6.5V10M12.5 3.5l-8.5 8.5"/></svg>';
      a.firstChild.textContent = app.label;
      panel.appendChild(a);
    }
    block.appendChild(panel);
    block.setAttribute('data-ezf-sent', confirm ? 'confirm' : 'sent');
    try { window.dispatchEvent(new CustomEvent('ezf:sent', { detail: { confirm: confirm } })); } catch (error) { /* old browsers: the gift popup still sees the panel */ }
  }

  // Klaviyo's own "submit" event also marks the block (a sign-up started some other way).
  var klaviyoHooked = false;
  function hookKlaviyo() {
    if (klaviyoHooked) return;
    klaviyoHooked = true;
    window.addEventListener('klaviyoForms', function (event) {
      var d = event.detail || {};
      if (d.type !== 'submit' || !d.formId) return;
      Array.prototype.forEach.call(document.querySelectorAll('[data-ezf-nl-ready]'), function (block) {
        if (block.querySelector("form[data-testid='klaviyo-form-" + d.formId + "']")) block._ezfTried = true;
      });
    });
  }

  function initNewsletter() {
    var root = document.querySelector('[data-ezf][data-ezf-fit]');
    if (!root) return;
    var texts = {
      required: root.getAttribute('data-email-required') || 'Please enter your email address.',
      invalid: root.getAttribute('data-email-invalid') || 'Enter a valid email, e.g. name@example.com',
      suggest: root.getAttribute('data-email-suggest') || 'Did you mean [email]?',
      copyHint: root.getAttribute('data-coupon-hint') || 'Tap the code to copy it.',
      copied: root.getAttribute('data-coupon-copied') || 'Copied!',
      copiedHint: root.getAttribute('data-coupon-copied-hint') || 'Code copied. Paste it at checkout.',
      copyFailed: root.getAttribute('data-coupon-copy-failed') || 'Couldn\u2019t copy automatically. The code is selected: copy it, or write it down for checkout.',
      // Ban-156: the words of the Welcome email (sender support@ezcustomize.com, code inside)
      sentTitle: 'Check your inbox',
      sentLead: 'Your 10% off code is in our Welcome email to',
      sentLeadNoEmail: 'Your 10% off code is in the Welcome email we just sent you.',
      sentTail: 'Look for support@ezcustomize.com, copy the code and use it at checkout.',
      sentFrom: 'Sent from support@ezcustomize.com',
      confirmTitle: 'Confirm your email',
      confirmLead: 'We sent a confirmation email to',
      confirmLeadNoEmail: 'We sent you a confirmation email.',
      confirmTail: 'Confirm it and your 10% off code arrives right after.',
      sentNote: 'Not there in a few minutes? Check your Promotions or Spam folder.',
      footTap: 'Tap Confirm in the email we sent to',
      footClick: 'Click Confirm in the email we sent to',
      footTapNoEmail: 'Tap Confirm in the email we just sent you.',
      footClickNoEmail: 'Click Confirm in the email we just sent you.',
      footNote: 'Not there? Check Promotions or Spam.'
    };
    hookKlaviyo();
    var blockInvalid = root.getAttribute('data-email-block') !== 'false';
    Array.prototype.forEach.call(document.querySelectorAll(NEWSLETTER), function (block) {
      if (block.hasAttribute('data-ezf-nl-ready')) return;
      block.setAttribute('data-ezf-nl-ready', '');
      initEmailCheck(block, texts, blockInvalid);
      var queued = false;
      var schedule = function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          enhanceCoupon(block, texts);
          sentPanel(block, texts);
          fitNewsletter(block);
        });
      };
      // Klaviyo renders asynchronously and swaps content on the success step.
      new MutationObserver(schedule).observe(block, { childList: true, subtree: true, characterData: true });
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(function (entries) {
          // Ban-126: remember the e-mail step's height in the footer (size from the observer, no layout read).
          var box = entries[0] && entries[0].borderBoxSize;
          box = box && (box[0] || box);
          if (box && box.blockSize && block.querySelector('input[type="email"]') && block.closest('.footer-container')) {
            block._ezfH = Math.round(box.blockSize);
          }
          schedule();
        }).observe(block);
      } else window.addEventListener('resize', schedule);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
      schedule();
    });
  }

  /* ------------------------------------------------------------------ */

  function initFooter(root) {
    if (!root || root.hasAttribute('data-ezf-ready')) return;

    var mq = window.matchMedia(MOBILE_QUERY);
    var applyLayout = initGroups(root, mq);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', applyLayout);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(applyLayout);
    }
    var pickerCleanup = null;
    root._ezfCleanup = function () {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', applyLayout);
      else if (typeof mq.removeListener === 'function') mq.removeListener(applyLayout);
      if (pickerCleanup) pickerCleanup();
      pickerCleanup = null;
      document.documentElement.classList.remove('ezf-cs-lock');
    };

    // No-JS fallback behaviour for the native select (used if enhancement is skipped).
    var select = root.querySelector('[data-ezf-country]');
    var submit = root.querySelector('[data-ezf-country-submit]');
    if (select && submit) {
      var initial = select.value;
      submit.hidden = true;
      select.addEventListener('change', function () {
        submit.hidden = select.value === initial;
      });
    }

    var form = root.querySelector('.ezf-locale');
    if (form) {
      try {
        pickerCleanup = initCountryPicker(form, mq) || null;
      } catch (error) {
        // Picker enhancement failed: fall back to the native select + Update button.
        Array.prototype.forEach.call(form.querySelectorAll('.ezf-cs__toggle, .ezf-cs__dialog'), function (el) { el.remove(); });
        form.classList.remove('is-enhanced');
        form.removeAttribute('data-ezf-cs-ready');
        document.documentElement.classList.remove('ezf-cs-lock');
        if (submit) submit.hidden = false;
        if (window.console && console.error) console.error('[ez-footer] country picker:', error);
      }
    }

    root.setAttribute('data-ezf-ready', '');
    initNewsletter();
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ezf]'), initFooter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-initialise when a section is re-rendered,
  // clean up when it is removed, and expand a group when its block is selected.
  document.addEventListener('shopify:section:load', function (event) {
    initFooter(event.target.querySelector('[data-ezf]'));
    initNewsletter();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-ezf]');
    if (root && root._ezfCleanup) root._ezfCleanup();
  });
  document.addEventListener('shopify:block:select', function (event) {
    if (event.target && event.target.classList && event.target.classList.contains('ezf-group')) {
      event.target.open = true;
    }
  });
})();
